// src/services/secureCookieAuthService.ts
import apiClient, { isAuthenticatedViaCookies } from './apiClient';
import { preloadService } from './preloadService';
import { 
  LoginRequest, 
  LoginResponse, 
  RegisterRequest, 
  RegisterResponse, 
  TwoFactorVerifyRequest,
  TwoFactorSetupResponse,
  TwoFactorEnableRequest,
  TwoFactorEnableResponse,
  UserInfoResponse,
  UserProfileUpdateRequest,
  UserProfileUpdateResponse,
  OpenAPILoginResponse,
  AppUser
} from '../features/auth/types';

// CSRF token management
let csrfToken: string | null = null;

// Function to get CSRF token from Django standard methods
const fetchCSRFToken = async (): Promise<string | null> => {
  try {
    // First try to get from meta tag (if set by backend)
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
      csrfToken = metaTag.getAttribute('content');
      return csrfToken;
    }

    // Try to get from cookie (Django standard)
    const cookieValue = getCsrfTokenFromCookie();
    if (cookieValue) {
      csrfToken = cookieValue;
      return csrfToken;
    }


    return null;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Helper function to get CSRF token from cookie
const getCsrfTokenFromCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  
  const name = 'csrftoken';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

export const secureCookieAuthService = {
  // Initialize CSRF token
  initCSRF: async (): Promise<void> => {
    await fetchCSRFToken();
  },

  // Registration
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    try {
      await fetchCSRFToken();
      const response = await apiClient.post<RegisterResponse>('/api/auth/register/', data);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  },

  // Login with cookie-based authentication
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    try {
      await fetchCSRFToken();
      const response = await apiClient.post<LoginResponse>('/api/auth/login/', data);
      
      // No need to manually store tokens - they're in httpOnly cookies
      // Just return the response data
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  },

  // 2FA verification
  verify2FA: async (data: TwoFactorVerifyRequest): Promise<LoginResponse> => {
    try {
      const response = await apiClient.post<LoginResponse>('/api/auth/2fa/verify/', data);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  },

  // 2FA setup
  setup2FA: async (): Promise<TwoFactorSetupResponse> => {
    try {
      const response = await apiClient.post<TwoFactorSetupResponse>('/api/auth/2fa/setup/');
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  },

  // Enable 2FA after verification
  enable2FA: async (request: TwoFactorEnableRequest): Promise<TwoFactorEnableResponse> => {
    try {
      const response = await apiClient.post<TwoFactorEnableResponse>(
        '/api/auth/2fa/enable/', 
        request
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/api/auth/logout/', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear any cached data
      preloadService.clearCache();
      csrfToken = null;
    }
  },

  // Get current user information
  getUserInfo: async (): Promise<UserInfoResponse> => {
    try {
      const response = await apiClient.get('/api/auth/user-info/');
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  },

  // Update user profile
  updateProfile: async (data: UserProfileUpdateRequest): Promise<UserProfileUpdateResponse> => {
    try {
      const response = await apiClient.put<UserProfileUpdateResponse>('/api/auth/user-profile/', data);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return isAuthenticatedViaCookies();
  },

  // OpenAPI-compliant methods
  loginOpenAPI: async (data: LoginRequest): Promise<OpenAPILoginResponse> => {
    try {
      await fetchCSRFToken();
      const response = await apiClient.post<OpenAPILoginResponse>('/api/auth/login/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  },

  getUserInfoOpenAPI: async (): Promise<AppUser> => {
    try {
      const response = await apiClient.get<AppUser>('/api/auth/user-info/');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get user info');
    }
  },

  setupTwoFactorOpenAPI: async (): Promise<TwoFactorSetupResponse> => {
    try {
      const response = await apiClient.post<TwoFactorSetupResponse>('/api/auth/2fa/setup/');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '2FA setup failed');
    }
  },

  enableTwoFactorOpenAPI: async (data: TwoFactorEnableRequest): Promise<{ message: string }> => {
    try {
      const response = await apiClient.post<{ message: string }>('/api/auth/2fa/enable/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '2FA enable failed');
    }
  }
};

export default secureCookieAuthService;